import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { CalendarDomain } from '../@shared/interfaces/calendar.interfaces';
import { calendarManagementService } from '../services/calendarManagement.service';
import { useAuthStore } from '../stores/auth.store';

interface ConnectCalendarParams {
  code: string;
  domain: CalendarDomain;
}

interface DisconnectCalendarParams {
  calendarId: string;
  domain: CalendarDomain;
}

export const useCalendarManagement = () => {
  const fetchProfile = useAuthStore((state) => state.fetchProfile);

  // Get OAuth URLs
  const getGoogleOAuthUrl = useMutation({
    mutationFn: () => calendarManagementService.getGoogleAuthUrl(),
  });

  const getOutlookOAuthUrl = useMutation({
    mutationFn: ({ state }: { state: string }) =>
      calendarManagementService.getOutlookAuthUrl(state),
  });

  // Connect calendars
  const connectCalendar = useMutation({
    mutationFn: ({ code, domain }: ConnectCalendarParams) =>
      calendarManagementService.connectCalendar(code, domain),
    onSuccess: async (_, { domain }) => {
      // Refresh profile data from Zustand store
      await fetchProfile();
      toast.success(`${domain} Calendar connected successfully!`);
    },
    onError: (error, { domain }) => {
      console.error(`Error connecting ${domain} Calendar:`, error);
      toast.error(`Failed to connect ${domain} Calendar`);
    },
  });

  // Disconnect calendar (unified)
  const disconnectCalendar = useMutation({
    mutationFn: ({ calendarId, domain }: DisconnectCalendarParams) =>
      calendarManagementService.disconnectCalendar(calendarId, domain),
    onSuccess: async () => {
      // Refresh profile data from Zustand store
      await fetchProfile();
      toast.success('Calendar disconnected successfully');
    },
    onError: (error) => {
      console.error('Error disconnecting calendar:', error);
      toast.error('Failed to disconnect calendar');
    },
  });

  return {
    // OAuth URLs
    getGoogleOAuthUrl,
    getOutlookOAuthUrl,

    // Connection management
    connectCalendar,
    disconnectCalendar,
  };
};
